'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';
import { isAdminUser } from '../config/adminConfig';

/**
 * SECURITY NOTE:
 * ─────────────────────────────────────────────────────────────────────────────
 * Frontend role checks (isAdmin, isApproved, canAccessApp) improve UX but do
 * NOT replace Supabase Row Level Security (RLS). Production security MUST rely
 * on Supabase Auth + RLS policies + no service role key in the frontend.
 *
 * User states:
 *   unauthenticated       — no Supabase session
 *   signed_up_pending     — access_status = 'pending'
 *   approved_member       — access_status = 'approved', is_admin = false
 *   approved_admin        — access_status = 'approved', is_admin = true
 *   rejected_or_disabled  — access_status = 'rejected'
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type UserApprovalStatus = 'pending' | 'approved' | 'rejected' | 'unknown';

interface AuthContextValue {
  user: any;
  session: any;
  loading: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  approvalStatus: UserApprovalStatus;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  /** True if user is authenticated AND approved (can access the app) */
  canAccessApp: () => boolean;
  /** True if user is authenticated, approved, AND is an admin */
  canManageUsers: () => boolean;
  /** Refresh approval status from DB (call after admin actions) */
  refreshApprovalStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<UserApprovalStatus>('unknown');
  const supabase = createClient();

  /**
   * Enriches the user object with DB-level flags from access_requests table.
   * This is the single source of truth for approval/admin status.
   */
  const enrichAndSetUser = async (authUser: any) => {
    if (!authUser) {
      setUser(null);
      setIsAdmin(false);
      setIsApproved(false);
      setApprovalStatus('unknown');
      return;
    }

    // Hardcoded primary admin is always approved
    const { ADMIN_EMAIL } = await import('../config/adminConfig');
    if (authUser.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
      setUser(authUser);
      setIsAdmin(true);
      setIsApproved(true);
      setApprovalStatus('approved');
      return;
    }

    // Look up access_requests for this user — source of truth for approval
    try {
      const { data } = await supabase
        .from('access_requests')
        .select('access_status, is_admin')
        .eq('email', authUser.email)
        .maybeSingle();

      const status = data?.access_status as UserApprovalStatus ?? 'unknown';
      const approved = status === 'approved';
      const admin = data?.is_admin === true;

      // Attach flags to user_metadata so isAdminUser() can read them
      const enriched = {
        ...authUser,
        user_metadata: {
          ...(authUser.user_metadata ?? {}),
          is_admin: admin,
          is_approved: approved,
        },
      };

      setUser(enriched);
      setIsAdmin(isAdminUser(enriched));
      setIsApproved(approved);
      setApprovalStatus(status);
    } catch {
      setUser(authUser);
      setIsAdmin(isAdminUser(authUser));
      setIsApproved(false);
      setApprovalStatus('unknown');
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      enrichAndSetUser(session?.user ?? null).finally(() => setLoading(false));
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      enrichAndSetUser(session?.user ?? null).finally(() => setLoading(false));
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Refresh approval status from DB — call after admin approval/rejection actions */
  const refreshApprovalStatus = async () => {
    if (user) {
      await enrichAndSetUser(user);
    }
  };

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;

    const userId = data.user?.id ?? null;
    const fullName = metadata?.fullName || email.split('@')[0];
    const reason = metadata?.reason || '';

    // Create an access_request row for new sign-ups (status: pending)
    try {
      await supabase.from('access_requests').upsert(
        {
          user_id: userId,
          email: email,
          full_name: fullName,
          access_status: 'pending',
          notes: reason || null,
        },
        { onConflict: 'email' }
      );
    } catch (_) {
      // Non-fatal — DB trigger may handle this
    }

    // Also create a user_profiles row (DB trigger does this too, but belt-and-suspenders)
    if (userId) {
      try {
        await supabase.from('user_profiles').upsert(
          {
            id: userId,
            email: email,
            full_name: fullName,
            is_approved: false,
            is_admin: false,
            role: 'pending',
          },
          { onConflict: 'id' }
        );
      } catch (_) {
        // Non-fatal — trigger will handle it
      }
    }

    return data;
  };

  // Email/Password Sign In — checks approval before allowing access
  const signIn = async (email: string, password: string) => {
    const { ADMIN_EMAIL } = await import('../config/adminConfig');

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;

    // Primary admin always gets through
    if (email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
      return data;
    }

    // Check approval status in access_requests — source of truth
    const { data: reqData } = await supabase
      .from('access_requests')
      .select('access_status, is_admin')
      .eq('email', email)
      .maybeSingle();

    if (!reqData) {
      // No access request found — sign them out and block
      await supabase.auth.signOut();
      throw new Error('PENDING_APPROVAL: Your account is awaiting admin approval. Please contact the club administrator.');
    }

    if (reqData.access_status === 'pending') {
      await supabase.auth.signOut();
      throw new Error('PENDING_APPROVAL: Your account is awaiting admin approval. You will be notified once access is granted.');
    }

    if (reqData.access_status === 'rejected') {
      await supabase.auth.signOut();
      throw new Error('ACCESS_REJECTED: Your access has been revoked. Please contact the club administrator or sign up again to request access.');
    }

    // access_status must be 'approved' to reach here
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  /** True if user is authenticated AND approved — use for route guards */
  const canAccessApp = (): boolean => {
    return !!user && isApproved;
  };

  /** True if user is authenticated, approved, AND is an admin */
  const canManageUsers = (): boolean => {
    return !!user && isApproved && isAdmin;
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    isAdmin,
    isApproved,
    approvalStatus,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    canAccessApp,
    canManageUsers,
    refreshApprovalStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
